import {useIntl} from 'react-intl'
import {useState} from 'react'

export type TaskChecklistItem = {
  content: string
  check: boolean
}

type Props = {
  items: Array<TaskChecklistItem>
  onChange: (items: Array<TaskChecklistItem>) => void
  disabled?: boolean
  onItemBlur?: () => void
}

/**
 * Editor de checklist em modo rascunho (sem chamada de API própria) — a lista fica inteira em
 * memória no array `items`/`onChange` de quem usa. Cada consumidor (criação, edição, modelo de
 * tarefa) decide o que fazer com o array atualizado: enviar tudo junto num submit, autosave
 * debounced, etc.
 */
const TaskChecklistEditor = ({items, onChange, disabled, onItemBlur}: Props) => {
  const intl = useIntl()
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const addCheck = () => {
    onChange([...items, {content: '', check: false}])
  }

  const removeCheck = (key: number) => {
    onChange(items.filter((_, index) => key !== index))
  }

  const updateCheck = (key: number, patch: Partial<TaskChecklistItem>) => {
    const next = [...items]
    next[key] = {...next[key], ...patch}
    onChange(next)
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index.toString())
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index)
    }
  }

  const handleDragOverWrapper = (index: number) => (e: React.DragEvent) => {
    handleDragOver(e, index)
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()

    try {
      const draggedIndexStr = e.dataTransfer.getData('text/plain')
      const draggedIndex = parseInt(draggedIndexStr)

      if (
        isNaN(draggedIndex) ||
        draggedIndex === dropIndex ||
        draggedIndex < 0 ||
        dropIndex < 0 ||
        draggedIndex >= items.length ||
        dropIndex >= items.length
      ) {
        setDraggedIndex(null)
        setDragOverIndex(null)
        return
      }

      const next = [...items]
      const [draggedItem] = next.splice(draggedIndex, 1)
      next.splice(dropIndex, 0, draggedItem)

      onChange(next)
      setDraggedIndex(null)
      setDragOverIndex(null)
    } catch (error) {
      setDraggedIndex(null)
      setDragOverIndex(null)
    }
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  return (
    <div className='checklist pt-6'>
      <div className='checklist-items'>
        {items.map((check, key) => (
          <div
            key={key}
            className={`check d-flex align-items-center ${
              draggedIndex === key ? 'dragging' : ''
            } ${dragOverIndex === key ? 'drag-over' : ''}`}
            onDragOver={handleDragOverWrapper(key)}
            onDrop={(e) => handleDrop(e, key)}
          >
            <div
              className='drag-handle me-3'
              draggable
              onDragStart={(e) => handleDragStart(e, key)}
              onDragEnd={handleDragEnd}
              title='Arrastar para reordenar'
            >
              <i className='bi bi-grip-vertical text-gray-500'></i>
            </div>

            <div className='flex-grow-1 d-flex align-items-center'>
              <input
                className='form-check-input me-3'
                type='checkbox'
                id={`check-${key}`}
                checked={check.check}
                onChange={(e) => updateCheck(key, {check: e.target.checked})}
                onBlur={onItemBlur}
                onKeyPress={(e) => {
                  e.key === 'Enter' && e.preventDefault()
                }}
              />

              <input
                className='form-control border-0 bg-transparent p-0'
                type='text'
                placeholder={intl.formatMessage({id: 'CHECKLIST.NAME_NEW'})}
                id={`input-${key}`}
                disabled={disabled}
                onKeyPress={(e) => {
                  e.key === 'Enter' && e.preventDefault()
                }}
                value={check.content}
                onChange={(e) => updateCheck(key, {content: e.target.value})}
                onBlur={onItemBlur}
                style={{flex: 1}}
              />
            </div>

            <button
              onClick={() => removeCheck(key)}
              className='btn p-0 ms-3'
              style={{flexShrink: 0, zIndex: 2, position: 'relative'}}
            >
              <i className='bi bi-trash3-fill text-danger'></i>
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={addCheck}
        onKeyPress={(e) => {
          e.key === 'Enter' && e.preventDefault()
        }}
        className='btn text-bold d-flex align-items-center p-0 mt-10'
      >
        <div className='d-flex align-items-center justify-content-center w-15px h-15px bg-gray-900 me-3'>
          <i className='bi bi-plus text-gray-100 fs-5 p-0'></i>
        </div>
        {intl.formatMessage({id: 'GENERAL.ADD_ITEM'})}
      </button>
    </div>
  )
}

export {TaskChecklistEditor}
